import React from 'react';
import { Link } from 'react-router-dom';
import { Blocks, Shield, Database, Zap, CheckCircle, ArrowRight, Building, Globe, TrendingUp, Users, Clock, Award, Link as LinkIcon } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { useBanner } from '../../contexts/BannerContext';
import { Breadcrumb } from '../../components/Breadcrumb';

export const CognitiveLedgerPage: React.FC = () => {
  useSEO('cognitive-ledger' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Smart Contracts",
      description: "Automated, self-executing contracts with built-in security and transparency for trustless transactions."
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Decentralized Applications",
      description: "Custom DApps built on leading blockchain platforms with seamless user experiences and robust functionality."
    },
    {
      icon: <LinkIcon className="w-6 h-6" />,
      title: "Supply Chain Transparency",
      description: "End-to-end traceability solutions that provide complete visibility into product journeys and authenticity."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Digital Identity Solutions",
      description: "Secure, decentralized identity management systems that give users control over their personal data."
    }
  ];

  const useCases = [
    {
      title: "Financial Services",
      description: "Cryptocurrency exchanges, DeFi platforms, payment processing, and regulatory compliance solutions"
    },
    {
      title: "Supply Chain Management", 
      description: "Product tracking, authenticity verification, and transparent supply chain monitoring"
    },
    {
      title: "Healthcare Records",
      description: "Secure patient data sharing, medical records management, and pharmaceutical traceability"
    },
    {
      title: "Real Estate",
      description: "Property tokenization, transparent transactions, and automated escrow services"
    },
    {
      title: "Digital Assets",
      description: "NFT marketplaces, digital collectibles, and intellectual property protection"
    },
    {
      title: "Legal & Compliance",
      description: "Document verification, audit trails, and automated regulatory compliance"
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enhanced Security",
      description: "Cryptographic security and immutable records"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Accessibility", 
      description: "Decentralized systems accessible worldwide"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Cost Reduction",
      description: "Eliminate intermediaries and reduce transaction costs"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 Operations",
      description: "Automated processes that work around the clock"
    }
  ];

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Blocks className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive Ledger
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Blockchain & Distributed Ledger Technology
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Transform your business with secure, transparent, and decentralized blockchain solutions. 
              From smart contracts and DApps to supply chain transparency and digital identity management, 
              we deliver enterprise-grade blockchain technology that drives innovation and trust.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Start Your Blockchain Journey
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <button
              onClick={() =>
                window.open(
                  "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                  "_blank"
                )
              }
              className="px-8 py-4 border-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-semibold rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-300"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900 dark:text-white">
            Blockchain Solutions & Services
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg dark:hover:shadow-2xl transition-all duration-300 text-center group"
              >
                <div className="text-blue-600 dark:text-blue-400 mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20 bg-gray-50 dark:bg-white/5 py-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900 dark:text-white">
            Industry Applications
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                  {useCase.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900 dark:text-white">
            Why Choose Blockchain Technology?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center group"
              >
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="text-white">
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20 bg-white/50 dark:bg-white/5 py-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900 dark:text-white">
            Blockchain Platforms & Technologies
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Ethereum</h3>
              <p className="text-gray-700 dark:text-gray-300">Smart contracts, DApps, and DeFi solutions</p>
            </div>
            
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Hyperledger</h3>
              <p className="text-gray-700 dark:text-gray-300">Enterprise blockchain frameworks</p>
            </div>
            
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Polygon</h3>
              <p className="text-gray-700 dark:text-gray-300">Scalable Ethereum solutions</p>
            </div>
            
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Solana</h3>
              <p className="text-gray-700 dark:text-gray-300">High-performance blockchain applications</p>
            </div>
            
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Binance Smart Chain</h3>
              <p className="text-gray-700 dark:text-gray-300">Fast and low-cost blockchain solutions</p>
            </div>
            
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Custom Networks</h3>
              <p className="text-gray-700 dark:text-gray-300">Private and consortium blockchain solutions</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Build on the Blockchain?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Transform your business with secure, transparent, and decentralized solutions. 
              Let's discuss how blockchain technology can drive innovation in your industry.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Discovery Call
              </button>
            </div>
          </div>
        </div>
      </section>
      </main>
    </>
  );
};